import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { errorMessage } from '../../../../lib/api-client';
import { useToasts } from '../../../../stores/toasts';

/**
 * Where one simple list's rows live in the cache: the query, how to read its rows, and how to put them back.
 * Admin redesign batch 2 — the shared pattern for Reasons, Sources, Custom fields, Pools and tags.
 */
export interface RowCache<TData, TRow> {
  readonly queryKey: QueryKey;
  readonly rowsOf: (data: TData) => readonly TRow[];
  readonly withRows: (data: TData, rows: readonly TRow[]) => TData;
  readonly idOf: (row: TRow) => string;
  /** Other screens that read what these rows change (the pickers' app settings, a record's custom fields). */
  readonly alsoRefresh?: readonly QueryKey[];
}

interface Versioned {
  readonly version: number;
}

/** Sends one change with the row's version at the moment it goes, and answers with the version the server gave it. */
export type SendChange = (version: number) => Promise<number>;

export interface RowChange<TRow> {
  readonly id: string;
  /** What the row reads like at once (CLAUDE.md §5a rule 1). */
  readonly patch: Partial<TRow>;
  readonly send: SendChange;
  /** The toast's headline if the server refuses: "The reason wasn't retired". */
  readonly failure: string;
  /** Also told of a refusal, to say it beside the field and give back what was typed. */
  readonly onRefused?: (cause: unknown) => void;
}

export interface RowChanges<TRow> {
  /** Applies at once, sends after any change still on its way for the same row, and puts back only its own patch if refused. */
  readonly change: (change: RowChange<TRow>) => Promise<boolean>;
  /**
   * The same, with a 10-second Undo (06-qol §4). Undo is a change of its own, queued behind this one, so it always goes
   * with the version this one came back with — never the one from before (review W16).
   */
  readonly changeWithUndo: (change: RowChange<TRow>, toast: { readonly message: string; readonly undo: RowChange<TRow> }) => void;
  /** Adds a row at once under a stand-in id; the saved row takes its place, or it goes again and the error is thrown. */
  readonly add: (placeholder: TRow, send: () => Promise<TRow>) => Promise<TRow>;
  /** The row as the cache holds it now. */
  readonly rowNow: (id: string) => TRow | undefined;
}

/** A stand-in row's id, so a page can draw it as still saving and offer nothing on it. */
export const PENDING_PREFIX = 'pending:';

export function isPendingRow(id: string): boolean {
  return id.startsWith(PENDING_PREFIX);
}

let nextPending = 0;

export function pendingId(): string {
  nextPending += 1;
  return `${PENDING_PREFIX}${nextPending}`;
}

/**
 * The optimistic engine every simple list shares (06-qol §4, ui-instant-feel rules 1–3):
 * - a change shows at once, on its own row only;
 * - changes to one row go to the server one after another, each with the version the last one returned, so a quick
 *   rename then Undo never sends a stale version;
 * - a refusal puts back only the fields this change set, and only where nothing newer has changed them since — never
 *   the whole list (review: "rollbacks that undo too much");
 * - the list is refetched once nothing is left on its way, so the optimistic rows are held until the truth lands.
 */
export function useRowChanges<TData, TRow extends Versioned>(cache: RowCache<TData, TRow>): RowChanges<TRow> {
  const queryClient = useQueryClient();
  const showToast = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const dismiss = useToasts((state) => state.dismiss);
  const chains = useRef(new Map<string, Promise<unknown>>());
  const inFlight = useRef(0);
  const { queryKey, rowsOf, withRows, idOf, alsoRefresh } = cache;

  const rowNow = useCallback(
    (id: string): TRow | undefined => {
      const data = queryClient.getQueryData<TData>(queryKey);
      return data === undefined ? undefined : rowsOf(data).find((row) => idOf(row) === id);
    },
    [queryClient, queryKey, rowsOf, idOf],
  );

  const writeRows = useCallback(
    (update: (rows: readonly TRow[]) => readonly TRow[]): void => {
      queryClient.setQueryData<TData>(queryKey, (data) => (data === undefined ? data : withRows(data, update(rowsOf(data)))));
    },
    [queryClient, queryKey, rowsOf, withRows],
  );

  const patchRow = useCallback(
    (id: string, update: (row: TRow) => TRow): void => writeRows((rows) => rows.map((row) => (idOf(row) === id ? update(row) : row))),
    [writeRows, idOf],
  );

  const settle = useCallback(async (): Promise<void> => {
    inFlight.current -= 1;
    if (inFlight.current > 0) return;
    await Promise.all([queryClient.invalidateQueries({ queryKey }), ...(alsoRefresh ?? []).map((key) => queryClient.invalidateQueries({ queryKey: key }))]);
  }, [queryClient, queryKey, alsoRefresh]);

  const change = useCallback(
    ({ id, patch, send, failure, onRefused }: RowChange<TRow>): Promise<boolean> => {
      const before = rowNow(id);
      if (!before) return Promise.resolve(false);
      const previous = fieldsOf(before, patch);
      void queryClient.cancelQueries({ queryKey });
      patchRow(id, (row) => ({ ...row, ...patch }));
      inFlight.current += 1;
      const prior = chains.current.get(id) ?? Promise.resolve();
      const run = prior.then(async (): Promise<boolean> => {
        const version = rowNow(id)?.version ?? before.version;
        try {
          const saved = await send(version);
          patchRow(id, (row) => ({ ...row, version: saved }));
          return true;
        } catch (cause) {
          patchRow(id, (row) => putBack(row, patch, previous));
          showToast({ tone: 'danger', message: failure, detail: errorMessage(cause) });
          onRefused?.(cause);
          return false;
        } finally {
          await settle();
        }
      });
      chains.current.set(
        id,
        run.catch(() => false),
      );
      return run;
    },
    [queryClient, queryKey, rowNow, patchRow, settle, showToast],
  );

  const changeWithUndo = useCallback(
    (first: RowChange<TRow>, toast: { readonly message: string; readonly undo: RowChange<TRow> }): void => {
      const toastId = showUndo(toast.message, () => void change(toast.undo));
      void change(first).then((isSaved) => {
        // A refused change has nothing to take back: its Undo goes with it.
        if (!isSaved) dismiss(toastId);
      });
    },
    [change, showUndo, dismiss],
  );

  const add = useCallback(
    async (placeholder: TRow, send: () => Promise<TRow>): Promise<TRow> => {
      const tempId = idOf(placeholder);
      void queryClient.cancelQueries({ queryKey });
      writeRows((rows) => [...rows, placeholder]);
      inFlight.current += 1;
      try {
        const saved = await send();
        writeRows((rows) => rows.map((row) => (idOf(row) === tempId ? saved : row)));
        return saved;
      } catch (cause) {
        writeRows((rows) => rows.filter((row) => idOf(row) !== tempId));
        throw cause;
      } finally {
        await settle();
      }
    },
    [queryClient, queryKey, writeRows, idOf, settle],
  );

  return { change, changeWithUndo, add, rowNow };
}

/** The row's own values of the fields a patch sets, to put back if it is refused. */
function fieldsOf<TRow>(row: TRow, patch: Partial<TRow>): Partial<TRow> {
  const out: Partial<TRow> = {};
  for (const key of Object.keys(patch) as (keyof TRow)[]) out[key] = row[key];
  return out;
}

/** Each field back as it was, unless something newer changed it after this patch. */
function putBack<TRow>(row: TRow, patch: Partial<TRow>, previous: Partial<TRow>): TRow {
  const next = { ...row };
  for (const key of Object.keys(patch) as (keyof TRow)[]) {
    if (JSON.stringify(row[key]) === JSON.stringify(patch[key])) next[key] = previous[key] as TRow[keyof TRow];
  }
  return next;
}
